import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { ArrowRight01Icon } from 'hugeicons-react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import '../../Styles/Chat.css';
import '../../Styles/Layout/InputArea.css';
import '../../Styles/Share/SharedChat.css';
import useDocumentTitle from '../../UseDocumentTitle';
import { Header } from '../Header/Header';
import MessagesList from '../Layout/Views/MessagesList';
import { InputContainer } from '../Layout/InputContainer';
import StudyPanel from '../Layout/Panels/StudyPanel';
import { MessagesSkeleton } from '../Layout/Skeletons';
import { NotFound } from '../Auth/404';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useMode } from '../../contexts/ModeContext';
import { useResourceContext } from '../../contexts/ResourceContext';
import { useWindow } from '../../contexts/WindowContext';
import useScrollHandler from '../../hooks/useScrollHandler';
import { ConvexMessage, MessageImage, SearchItem } from '../../types/message';
import type { MessageBubbleProps } from '../Message/MessageBubble';

interface SharedMessage {
  key: string;
  isAi: boolean;
  content: string;
  timestamp: number;
  isGenerating: boolean;
  isVoice?: boolean;
  model?: string | null;
  images: MessageImage[];
  textAttachments: { title: string; type: string }[];
  searchItems?: SearchItem[];
}

interface SharedChatData {
  title: string;
  createdAt: string;
  messages: SharedMessage[];
}

const noop = () => {};
const noopAsync = async () => {};

// Shared chats are read-only, so the edit plumbing MessagesList threads to
// each bubble is inert.
const readOnlyEditActions: MessageBubbleProps['editActions'] = {
  handleEditSubmit: () => false,
  setEditContent: noop,
};
const readOnlyMessageActions = { RegenerateMessage: noopAsync };
const readOnlyRenderFunctions: MessageBubbleProps['renderFunctions'] = {
  EditArea: () => null,
  editContent: '',
  setEditContent: noop,
  editMode: undefined as unknown as MessageBubbleProps['renderFunctions']['editMode'],
  setEditMode: noop,
  editCurrentModel: null,
  setEditCurrentModel: noop,
  prepareEditContent: noop,
  editTextFiles: [],
  editImageFiles: [],
  addEditFiles: noop,
  removeEditAttachment: noop,
};

/**
 * Rebuilds shared rows as regular ConvexMessages so they render through the
 * normal MessagesList. IDs are synthetic and scoped to the share; the backend
 * never publishes real message IDs.
 */
const toConvexMessages = (shareId: string, messages: SharedMessage[]): ConvexMessage[] =>
  messages.map(message => ({
    _id: `shared-${shareId}-${message.key}`,
    _creationTime: message.timestamp,
    chatId: `shared-${shareId}`,
    content: message.content,
    isAi: message.isAi,
    timestamp: message.timestamp,
    isGenerating: message.isGenerating,
    isVoice: message.isVoice ?? false,
    model: message.model ?? null,
    images: message.images,
    textAttachments: message.textAttachments.map(doc => ({ title: doc.title, type: doc.type, url: '' })),
    searchItems: message.searchItems ?? [],
    branchId: 1,
    messagePairId: `shared-${message.key}`,
  } as unknown as ConvexMessage));

const ContinueBar: React.FC<{ isForking: boolean; isSignedIn: boolean; onContinue: () => void }> = ({
  isForking,
  isSignedIn,
  onContinue,
}) => (
  <div className="input-wrapper chat shared-continue-wrapper">
    <div className="input-capsule shared-continue-capsule">
      <div className="shared-continue-text">
        <span className="shared-continue-title">Shared Chat</span>
        <span className="shared-continue-subtitle">
          {isSignedIn
            ? 'Continue it to keep chatting in a copy of your own.'
            : 'Sign in to continue this conversation.'}
        </span>
      </div>
      <button
        type="button"
        className="shared-continue-button"
        onClick={onContinue}
        disabled={isForking}
      >
        {isForking ? 'Copying…' : isSignedIn ? 'Continue This Chat' : 'Sign In to Continue'}
        {!isForking && <ArrowRight01Icon size={16} aria-hidden />}
      </button>
    </div>
  </div>
);

const SharedChat: React.FC = () => {
  const { shareId = '' } = useParams<{ shareId: string }>();
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useMode();
  const { windowWidth } = useWindow();
  const { isResourcesVisible, handleResourceClick, setSharedResourceSource } = useResourceContext();
  const [isForking, setIsForking] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const resourcePanelRef = useRef<ImperativePanelHandle>(null);
  const lastResourcePanelSizeRef = useRef(50);

  const sharedChat: SharedChatData | null | undefined = useQuery(
    'chats:getSharedChat' as any,
    shareId ? { shareId } : 'skip'
  );
  const forkSharedChat = useMutation('chats:forkSharedChat' as any);

  useDocumentTitle(sharedChat?.title || 'StudyBuddy');

  const messages = useMemo(
    () => (sharedChat ? toConvexMessages(shareId, sharedChat.messages) : []),
    [sharedChat, shareId]
  );

  // Feed this chat's messages to the resource panel while the page is open,
  // so resource cards open in the side panel like they do in a normal chat.
  useEffect(() => {
    if (!sharedChat) {
      // Unshared while open: drop its resources along with the messages.
      if (sharedChat === null) setSharedResourceSource(null);
      return;
    }
    setSharedResourceSource({ chatId: `shared-${shareId}`, messages });
  }, [sharedChat, shareId, messages, setSharedResourceSource]);

  useEffect(() => () => setSharedResourceSource(null), [setSharedResourceSource]);

  // Same collapse/restore behaviour as ChatContainer's resource panel.
  useEffect(() => {
    if (windowWidth < 768) return;
    const panel = resourcePanelRef.current;
    if (!panel) return;

    const frame = window.requestAnimationFrame(() => {
      if (isResourcesVisible) {
        panel.resize(lastResourcePanelSizeRef.current);
      } else {
        const currentSize = panel.getSize();
        if (currentSize > 0) lastResourcePanelSizeRef.current = currentSize;
        panel.collapse();
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isResourcesVisible, windowWidth]);

  const { ScrollToBottomButton, Spacer } = useScrollHandler({
    messagesContainerRef: messagesContainerRef as React.RefObject<HTMLDivElement>,
    isGeneratingResponse: false,
    chatId: `shared-${shareId}`,
    messages,
  });

  const handleContinue = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user?._id) {
      // Come back to this shared chat after signing in.
      auth.login({ appState: { returnTo: location.pathname } });
      return;
    }
    setIsForking(true);
    try {
      const { chatId } = await forkSharedChat({ shareId, userId: String(auth.user._id) });
      navigate(`/${chatId}`);
    } catch (error) {
      console.error('Error continuing shared chat:', error);
      toast('Failed to Continue Chat', 'error');
    } finally {
      setIsForking(false);
    }
  }, [auth, forkSharedChat, shareId, navigate, location.pathname, toast]);

  if (sharedChat === null) {
    return <NotFound message="This shared chat doesn't exist or is no longer shared." />;
  }

  const header = <Header sharedChatTitle={sharedChat?.title ?? ''} />;

  const messagesList = sharedChat === undefined ? (
    <div className="messages-wrapper">
      <MessagesSkeleton />
    </div>
  ) : (
    <MessagesList
      renderDefaultDisplay={() => <div className="messages-wrapper" />}
      chatId={null}
      handleResourceClick={handleResourceClick}
      memoizedEditActions={readOnlyEditActions}
      memoizedMessageActions={readOnlyMessageActions}
      mode={mode}
      getRenderFunctions={() => readOnlyRenderFunctions}
      messagesContainerRef={messagesContainerRef as React.RefObject<HTMLDivElement>}
      filteredMessages={messages}
      Spacer={Spacer as () => React.ReactElement}
      readOnly
    />
  );

  const inputContainer = sharedChat ? (
    <InputContainer
      filteredMessages={messages}
      isSubmitting={false}
      ScrollToBottomButton={ScrollToBottomButton}
      showAuthBox={false}
      InputArea={
        <ContinueBar
          isForking={isForking}
          isSignedIn={auth.isAuthenticated}
          onContinue={handleContinue}
        />
      }
    />
  ) : null;

  // Selection actions are hidden in read-only mode, so these never fire.
  const studyPanel = <StudyPanel setInput={noop} triggerSubmit={noop} />;

  if (windowWidth >= 768) {
    return (
      <div className="chat-page">
        <PanelGroup direction="horizontal" className="chat-area">
          <Panel className="chat-message-panel" defaultSize={isResourcesVisible ? 50 : 100} minSize={30}>
            <div className="study-view-container">
              {header}
              {messagesList}
              {inputContainer}
            </div>
          </Panel>
          <PanelResizeHandle
            className={`resize-handle${isResourcesVisible ? '' : ' is-hidden'}`}
            disabled={!isResourcesVisible}
          />
          <Panel
            ref={resourcePanelRef}
            className="study-resource-panel"
            defaultSize={isResourcesVisible ? 50 : 0}
            minSize={30}
            collapsedSize={0}
            collapsible
            onResize={(size) => {
              if (isResourcesVisible && size >= 30) {
                lastResourcePanelSizeRef.current = size;
              }
            }}
          >
            <div className="study-panel-container">{studyPanel}</div>
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  return (
    <div className="chat-page">
      <div className="chat-area">
        {header}
        <div className="study-view-container">
          {messagesList}
          {inputContainer}
        </div>
        <div className="study-panel-container">{studyPanel}</div>
      </div>
    </div>
  );
};

export default SharedChat;
